import type { GetUserWorldCupPredictionResponse, WorldCupPredictionRound } from '@wc26-forecast/api/types';
import type {
  WorldCupBestThirdSlot,
  WorldCupForecastState,
  WorldCupGroupId,
  WorldCupGroupPlacement,
  WorldCupMatchId,
  WorldCupMatchSource,
  WorldCupPredictionState,
  WorldCupRoundType,
  WorldCupTeamId,
} from '@wc26-forecast/model/types';

const bestThirdSlots: WorldCupBestThirdSlot[] = [
  'BEST_THIRD_1',
  'BEST_THIRD_2',
  'BEST_THIRD_3',
  'BEST_THIRD_4',
  'BEST_THIRD_5',
  'BEST_THIRD_6',
  'BEST_THIRD_7',
  'BEST_THIRD_8',
];

const initialBestThirdTeams = bestThirdSlots.reduce((acc, slot) => {
  acc[slot] = null;
  return acc;
}, {} as WorldCupPredictionState['bestThirdTeams']);

export function mapUserPredictionToWorldCupState(
  response: GetUserWorldCupPredictionResponse,
  state: WorldCupForecastState,
): WorldCupPredictionState {
  const prediction = response.body;
  const findTeamId = (teamCode: string): WorldCupTeamId | null => {
    const team = Object
      .values(state.teams.byId)
      .find((item) => item.code === teamCode);
    return team?.id ?? null;
  };
  const getMatchIdsByRound = (round: WorldCupRoundType): WorldCupMatchId[] => {
    return state.matches.ids
      .filter((matchId) => {
        const match = state.matches.byId[matchId];
        return match?.round === round;
      });
  };
  const groupPlacements: WorldCupPredictionState['groupPlacements'] = {};
  state.groups.ids.forEach((groupId) => {
    const groupTeams = prediction.groups[groupId];
    if (!groupTeams?.length) {
      return;
    }
    const placements: WorldCupGroupPlacement = {};
    groupTeams
      .forEach((team) => {
        const teamId = findTeamId(team.name);
        if (!teamId) {
          return;
        }
        placements[team.position] = teamId;
      });
    groupPlacements[groupId] = placements;
  });
  const thirdPlaceTeamGroupById = new Map<WorldCupTeamId, WorldCupGroupId>();
  Object
    .entries(prediction.thirdPlace)
    .forEach(([ groupId, team ]) => {
      if (!team?.isSelected) {
        return;
      }
      const teamId = findTeamId(team.name);
      if (!teamId) {
        return;
      }
      thirdPlaceTeamGroupById.set(teamId, groupId as WorldCupGroupId);
    });
  const bestThirdTeams: WorldCupPredictionState['bestThirdTeams'] = {
    ...initialBestThirdTeams,
  };
  const resolveSourceTeamId = (source: WorldCupMatchSource): WorldCupTeamId | null => {
    if (source.type === 'GROUP_PLACE') {
      return groupPlacements[source.group]?.[source.place] ?? null;
    }
    if (source.type === 'BEST_THIRD') {
      return bestThirdTeams[source.slot] ?? null;
    }
    if (source.type === 'MATCH_WINNER') {
      return matchResults[source.matchId]?.winnerTeamId ?? null;
    }
    if (source.type === 'MATCH_LOSER') {
      return matchResults[source.matchId]?.loserTeamId ?? null;
    }
    return null;
  };
  const assignBestThirdSlots = () => {
    Object
      .values(prediction.final_1_16)
      .forEach((roundMatch) => {
        if (!roundMatch) {
          return;
        }
        const place1TeamId = findTeamId(roundMatch.place1.name);
        const place2TeamId = findTeamId(roundMatch.place2.name);
        if (!place1TeamId || !place2TeamId) {
          return;
        }
        getMatchIdsByRound('ROUND_OF_32')
          .forEach((matchId) => {
            const match = state.matches.byId[matchId];
            if (!match) {
              return;
            }
            const homeTeamId = resolveSourceTeamId(match.homeSource);
            const awayTeamId = resolveSourceTeamId(match.awaySource);
            const tryAssign = (
              source: WorldCupMatchSource,
              knownTeamId: WorldCupTeamId | null,
              thirdTeamId: WorldCupTeamId,
            ) => {
              if (source.type !== 'BEST_THIRD') {
                return;
              }
              if (
                knownTeamId !== place1TeamId &&
                knownTeamId !== place2TeamId
              ) {
                return;
              }
              const groupId = thirdPlaceTeamGroupById.get(thirdTeamId);
              if (!groupId) {
                return;
              }
              const allowedGroups = state.bestThirdRules[source.slot];
              if (!allowedGroups?.includes(groupId)) {
                return;
              }
              bestThirdTeams[source.slot] = thirdTeamId;
            };
            if (homeTeamId === place1TeamId) {
              tryAssign(match.awaySource, homeTeamId, place2TeamId);
            }
            if (homeTeamId === place2TeamId) {
              tryAssign(match.awaySource, homeTeamId, place1TeamId);
            }
            if (awayTeamId === place1TeamId) {
              tryAssign(match.homeSource, awayTeamId, place2TeamId);
            }
            if (awayTeamId === place2TeamId) {
              tryAssign(match.homeSource, awayTeamId, place1TeamId);
            }
          });
      });
  };
  const matchResults: WorldCupPredictionState['matchResults'] = {};
  assignBestThirdSlots();
  const findMatchIdByTeams = (
    round: WorldCupRoundType,
    team1Id: WorldCupTeamId,
    team2Id: WorldCupTeamId,
  ): WorldCupMatchId | null => {
    const match = getMatchIdsByRound(round)
      .map((matchId) => state.matches.byId[matchId])
      .find((item) => {
        if (!item) {
          return false;
        }
        const homeTeamId = resolveSourceTeamId(item.homeSource);
        const awayTeamId = resolveSourceTeamId(item.awaySource);
        return (
          homeTeamId === team1Id &&
          awayTeamId === team2Id
        ) || (
          homeTeamId === team2Id &&
          awayTeamId === team1Id
        );
      });
    return match?.id ?? null;
  };
  const mapRoundResults = (
    round: WorldCupPredictionRound,
    roundType: WorldCupRoundType,
  ) => {
    Object.values(round).forEach((roundMatch) => {
      if (!roundMatch) {
        return;
      }
      const place1TeamId = findTeamId(roundMatch.place1.name);
      const place2TeamId = findTeamId(roundMatch.place2.name);
      if (!place1TeamId || !place2TeamId) {
        return;
      }
      const matchId = findMatchIdByTeams(
        roundType,
        place1TeamId,
        place2TeamId,
      );
      if (!matchId) {
        return;
      }
      const winnerTeamId = roundMatch.place1.isSelected
        ? place1TeamId
        : roundMatch.place2.isSelected
          ? place2TeamId
          : null;
      const loserTeamId = roundMatch.place1.isSelected
        ? place2TeamId
        : roundMatch.place2.isSelected
          ? place1TeamId
          : null;
      if (!winnerTeamId || !loserTeamId) {
        return;
      }
      matchResults[matchId] = {
        winnerTeamId,
        loserTeamId,
      };
    });
  };
  mapRoundResults(prediction.final_1_16, 'ROUND_OF_32');
  mapRoundResults(prediction.final_1_8, 'ROUND_OF_16');
  mapRoundResults(prediction.final_1_4, 'QUARTER_FINAL');
  mapRoundResults(prediction.final_1_2, 'SEMI_FINAL');
  const { team1, team2 } = prediction.third_place_match;
  const thirdPlaceTeam1Id = findTeamId(team1.name);
  const thirdPlaceTeam2Id = findTeamId(team2.name);
  if (thirdPlaceTeam1Id && thirdPlaceTeam2Id) {
    const thirdPlaceMatchId = findMatchIdByTeams(
      'THIRD_PLACE',
      thirdPlaceTeam1Id,
      thirdPlaceTeam2Id,
    );
    const winnerTeamId = team1.isSelected
      ? thirdPlaceTeam1Id
      : team2.isSelected
        ? thirdPlaceTeam2Id
        : null;
    const loserTeamId = team1.isSelected
      ? thirdPlaceTeam2Id
      : team2.isSelected
        ? thirdPlaceTeam1Id
        : null;
    if (thirdPlaceMatchId && winnerTeamId && loserTeamId) {
      matchResults[thirdPlaceMatchId] = {
        winnerTeamId,
        loserTeamId,
      };
    }
  }
  return {
    groupPlacements,
    bestThirdTeams,
    matchResults,
    finalScorePrediction: {
      homeScore: prediction.final.result1,
      awayScore: prediction.final.result2,
    },
    hasUserPrediction: true,
  };
}